/* ChicChac — sidebar (hamburger) menu, founder change-set v3.

   The brief asks for a fixed structure:
       Profile · My Orders · Categories ▾ · Chic Deals ▾ · Gifting Needs ▾ ·
       Know us better ▾ · Contact Us
   with every accordion open by default. (Categories arrived in v5.)

   Two reasons this is built here rather than as a Shopify navigation menu:
     1. the Admin API token is scoped to products + inventory, so it cannot
        write online-store navigation at all; and
     2. Horizon's drawer accordion only supports expanding the *first* group
        (drawer_accordion_expand_first), and the brief wants both open.

   The drawer's own markup is replaced wholesale, so nothing here depends on
   what the `main-menu` linklist happens to contain. If the founder later wants
   to drive this from Admin navigation instead, delete this file and the
   <script> tag in theme.liquid and the stock drawer comes back.
*/
(function () {
  'use strict';

  var CC = window.CC || {};

  var ICON = {
    user: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="8" r="3.6"/><path d="M4.5 20a7.5 7.5 0 0 1 15 0" stroke-linecap="round"/></svg>',
    mail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="5.5" width="18" height="13" rx="2.5"/><path d="M3.8 7.2 12 13l8.2-5.8" stroke-linecap="round"/></svg>',
    chevron: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M6 9.5 12 15l6-5.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    exit: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8"/><path d="M17 15.5 20.5 12 17 8.5M20 12H9.5"/></svg>',
    parcel: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9z"/><path d="M3 7.5 12 12l9-4.5M12 12v9"/></svg>'
  };

  function menuModel() {
    /* Below 750px the header's account icon is hidden (a 96px logo plus four
       44px actions overflows a 390px phone), so this drawer is the ONLY account
       entry point on mobile — which is most of the traffic. That makes the
       signed-in/out distinction worth drawing here rather than sending everyone
       to a "Profile" row that silently bounces to a login screen. */
    var signedIn = CC.loggedIn === true;
    return [
      {
        kind: 'link',
        label: signedIn ? 'Profile' : 'Sign in / Sign up',
        href: CC.accountUrl || '/account',
        icon: ICON.user
      },
      /* 2026-08-17: order-tracking audit (docs/launch/order-tracking-audit-
         2026-08-17.md) found "My Orders" and "Track my order" as two separate
         rows resolving to the byte-identical URL — a customer who reads two
         different promises and gets one destination twice. That is the actual
         defect, not the missing tracking page (there genuinely isn't one to
         link yet: no carrier tracking exists, and Shopify's per-order
         status page is emailed, not linkable from a static menu).

         Fix is to stop pretending there are two destinations until there are.
         One row survives, kept as "Track my order" rather than "My Orders"
         because the founder deliberately promoted that exact phrasing to
         top-level on 2026-08-13 as "the phrasing a customer actually scans
         for" — the 11pm shopper wondering where their order is searches for
         "track", not "my orders". Nothing reachable is lost: it's still the
         account order-history page, which is where fulfilment/tracking status
         will show once an order ships (Shiprocket AWB sync → Shopify order),
         and "Profile" above still opens the same hosted account portal.

         The CC.trackUrl fallback is kept exactly as before — once
         cc_track_url is set in theme settings to a real, distinct tracking
         destination, THIS row's href updates to it automatically, no code
         change needed. If a dedicated "My Orders" shortcut is wanted back
         at that point (now that it would point somewhere different from
         tracking), it's a one-line addition, not a redesign. */
      { kind: 'link', label: 'Track my order', href: CC.trackUrl || CC.ordersUrl || '/account', icon: ICON.parcel },
      /* Founder change-set v5: "Add Categories accordion below my orders."
         Only destinations that actually resolve are listed. Bracelets, Neck
         Pieces and Pearls were held back until 2026-08-15 because they were
         homepage tiles with no collection behind them; the v2 catalogue stocks
         all three, so they are listed now. Best Sellers is still absent — it is
         a tag no product carries, and it should stay that way until real sales
         data says which pieces earn it. Men is included because v5 creates it,
         deliberately empty, behind a "coming soon" screen.

         Order matches the homepage category rail and the workbook's Shop By
         Category sheet. */
      {
        kind: 'group', label: 'Categories', open: true,
        links: [
          { label: 'New Arrivals',  href: '/collections/new-arrivals' },
          { label: 'Earrings',      href: '/collections/earrings' },
          { label: 'Bracelets',     href: '/collections/bracelets' },
          { label: 'Finger Rings',  href: '/collections/finger-rings' },
          { label: 'Neck Pieces',   href: '/collections/neck-pieces' },
          { label: 'Pearls',        href: '/collections/pearls' },
          { label: 'Men — Coming soon', href: '/collections/men' }
        ]
      },
      {
        kind: 'group', label: 'Chic Deals', open: true,
        links: [
          /* "Budget Beauties | Under 399" was held back while the cheapest SKU
             was ₹405, which would have linked to an empty page. The v2
             catalogue puts 44 products under ₹399 (cheapest ₹324), so the row
             is live. */
          { label: 'Budget Beauties | Under 399', href: '/collections/budget-beauties-under-399' },
          { label: 'Style Steals | Under 599',    href: '/collections/style-steals-under-599' },
          { label: 'Elite Picks | Under 999',     href: '/collections/elite-picks-under-999' }
        ]
      },
      {
        kind: 'group', label: 'Gifting Needs', open: true,
        links: [
          { label: 'Birthday Special',          href: '/collections/birthday-special' },
          { label: 'Girls Squad',               href: '/collections/girls-squad' },
          { label: 'Graduation & Achievements', href: '/collections/graduation-achievements' },
          { label: 'Festivities',               href: '/collections/festivities' },
          { label: 'Thank you gifts',           href: '/collections/thank-you-gifts' }
        ]
      },
      /* 2026-08-13: the "Know us better" group was REMOVED from this drawer.
         Its story content now lives on the homepage (templates/index.json →
         cc_story, directly after Why ChicChac), so the drawer no longer needs a
         route to it.

         Nothing was lost by deleting the whole group:
           • "Contact us" duplicated the icon'd top-level Contact Us below.
           • "Instagram" is still in the footer, with its icon.
           • "Track your order" pointed at
             https://shopify.com/<id>/account/orders — the EXACT same URL as the
             top-level "My Orders" item, verified live before removal. (2026-08-17:
             that same duplicate-URL problem was later found between the top-level
             "My Orders" and "Track my order" rows themselves and fixed by merging
             them — see the comment on the surviving "Track my order" row above.)
           • The group heading was a plain <summary>, never a link, so no route
             to /pages/know-us-better existed here to lose.

         If order tracking ever needs to be distinct from account order history
         (a carrier tracking page, say), add it as a top-level `kind: 'link'`
         rather than resurrecting this group. */
      /* 2026-08-13: relabelled 'Contact Us' -> 'Write to Us' at the founder's
         request. This now matches the heading the contact page itself renders
         (sections/cc-contact.liquid), so the drawer promises the same words the
         destination shows. Href is unchanged. */
      { kind: 'link', label: 'Write to Us', href: '/pages/contact', icon: ICON.mail }
    ].concat(
      /* Sign-out only exists on the hosted account portal, which a phone user
         has no other way back to once the header icon is gone. */
      signedIn
        ? [{ kind: 'link', label: 'Sign out', href: CC.logoutUrl || '/account/logout', icon: ICON.exit }]
        : []
    );
  }

  function linkEl(item, cls) {
    var a = document.createElement('a');
    a.className = cls;
    a.href = item.href;
    if (item.icon) {
      var i = document.createElement('span');
      i.className = 'cc-menu__icon';
      i.innerHTML = item.icon;          /* icon constants only — never user data */
      a.appendChild(i);
    }
    var t = document.createElement('span');
    t.textContent = item.label;         /* labels go in as text, not markup */
    a.appendChild(t);
    return a;
  }

  function groupEl(item) {
    var d = document.createElement('details');
    d.className = 'cc-menu__acc';
    /* `open` alone does not survive: the drawer is itself a
       <details class="menu-drawer-container">, and Horizon's header-drawer
       collapses every nested <details> when the outer one is closed — which it
       is on page load, i.e. before anyone has seen the menu. The flag records
       the intent so reassertOpen() can restore it each time the drawer opens.
       See reassertOpen() below. */
    if (item.open) {
      d.open = true;
      d.setAttribute('data-cc-open', '1');
    }

    var s = document.createElement('summary');
    s.className = 'cc-menu__sum';
    var label = document.createElement('span');
    label.textContent = item.label;
    var chev = document.createElement('span');
    chev.className = 'cc-menu__chev';
    chev.innerHTML = ICON.chevron;
    s.appendChild(label);
    s.appendChild(chev);

    var ul = document.createElement('ul');
    ul.className = 'cc-menu__sub';
    ul.setAttribute('role', 'list');
    item.links.forEach(function (l) {
      var li = document.createElement('li');
      li.appendChild(linkEl(l, 'cc-menu__sublink'));
      ul.appendChild(li);
    });

    d.appendChild(s);
    d.appendChild(ul);
    return d;
  }

  function build(list) {
    list.textContent = '';
    list.classList.add('cc-menu');
    menuModel().forEach(function (item) {
      var li = document.createElement('li');
      li.className = 'cc-menu__item';
      if (item.kind === 'group') {
        li.classList.add('cc-menu__item--group');
        li.appendChild(groupEl(item));
      } else {
        li.appendChild(linkEl(item, 'cc-menu__link'));
      }
      list.appendChild(li);
    });
    list.dataset.ccMenu = '1';
  }

  /* Restore the groups the brief wants expanded. Runs on every open rather than
     once, because Horizon collapses them again on every close. A shopper who
     deliberately collapsed a group during THIS visit is not overridden — the
     flag is cleared the first time they toggle it shut. */
  function reassertOpen(root) {
    (root || document).querySelectorAll('.cc-menu__acc[data-cc-open]').forEach(function (d) {
      d.open = true;
    });
  }

  /* A2-001: opening the drawer never moved focus into it, and closing it
     dropped focus on <body> instead of back onto the hamburger.

     Root cause, confirmed live: clicking the <summary> toggles the <details>
     open (native disclosure behaviour) and focus stays ON the summary —
     Horizon's own header-drawer.js calls trapFocus(details) afterwards, but
     trapFocus() only ever calls container.focus(), which is a silent no-op
     on a <details> (not focusable by default), so it never actually MOVES
     focus anywhere; it just starts trapping wherever focus already is. That
     is enough for Tab to behave, but Escape only closes the drawer while
     focus is somewhere inside <header-drawer> at all — fine here since the
     summary itself qualifies — and on close, header-drawer.js's #close()
     removes the `open` attribute but never focuses anything, so the browser
     drops focus to <body>.

     Fix lives entirely in the existing toggle listener below: move focus to
     the drawer's own close button on open (same pattern cc-wishlist.js uses
     for its drawer), and back to the summary on close. Both run through the
     native `toggle` event so they cover mouse AND keyboard activation alike,
     and neither fights Horizon's own trap — the close button is inside the
     trapped container, and by the time `toggle` fires on close, Horizon has
     already torn its trap down (see header-drawer.js #close()). */
  function focusIntoDrawer(outer) {
    var close = outer.querySelector('.menu-drawer__close-button');
    if (close) close.focus();
  }

  function focusTrigger(outer) {
    var trigger = outer.querySelector('summary.header__icon--menu');
    if (trigger) trigger.focus();
  }

  function watchDrawer(list) {
    var outer = list.closest('details.menu-drawer-container');
    if (!outer || outer.__ccWatched) return;
    outer.__ccWatched = true;
    outer.addEventListener('toggle', function () {
      if (outer.open) {
        reassertOpen(outer);
        focusIntoDrawer(outer);
      } else {
        focusTrigger(outer);
      }
    });
    /* A group closed by hand stops being reasserted. Captured on the way down
       because <details> fires `toggle` on the element itself, not on a parent. */
    outer.addEventListener('toggle', function (e) {
      var d = e.target;
      if (d !== outer && d.classList && d.classList.contains('cc-menu__acc') && !d.open) {
        d.removeAttribute('data-cc-open');
      }
    }, true);
    /* Reassert-only on init — this can run with the drawer already open (a
       Section Rendering API refresh replacing the header mid-session), and
       stealing focus at that point, with no user interaction just having
       happened, would be its own bug. */
    if (outer.open) reassertOpen(outer);
  }

  function apply() {
    document
      .querySelectorAll('.menu-drawer__navigation .menu-drawer__menu:not([data-cc-menu])')
      .forEach(function (list) {
        build(list);
        watchDrawer(list);
      });
  }

  if (document.readyState !== 'loading') apply();
  else document.addEventListener('DOMContentLoaded', apply);

  /* Horizon re-renders the header group through the Section Rendering API (a
     cart update is enough), which drops a stock drawer back in. Re-apply when
     that happens; the [data-cc-menu] guard keeps it from rebuilding endlessly. */
  new MutationObserver(apply).observe(document.documentElement, { childList: true, subtree: true });
})();
